import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Email as EmailIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Block as SuspendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getCreators,
  createCreator,
  saveCreatorAsPending,
  approveCreator,
  rejectCreator,
  suspendCreator,
  resendCreatorWelcome,
  Creator,
  CreateCreatorData,
} from '../adminApi';

type StatusFilter = 'all' | 'pending' | 'approved' | 'active' | 'suspended' | 'rejected';

const CreatorList: React.FC = () => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [terminationType, setTerminationType] = useState<'for_cause' | 'without_cause'>('without_cause');
  const [actionLoading, setActionLoading] = useState(false);

  // New creator form
  const [newCreator, setNewCreator] = useState<CreateCreatorData>({
    email: '',
    name: '',
    bio: '',
    website_url: '',
    niche: '',
    notes: '',
    social_links: {
      instagram: '',
      tiktok: '',
      facebook: '',
      twitter: '',
    },
  });

  const resetNewCreatorForm = () => {
    setNewCreator({
      email: '',
      name: '',
      bio: '',
      website_url: '',
      niche: '',
      notes: '',
      social_links: {
        instagram: '',
        tiktok: '',
        facebook: '',
        twitter: '',
      },
    });
  };

  const fetchCreators = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCreators({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setCreators(result.creators || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load creators');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusChip = (status: Creator['status']) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      active: 'success',
      approved: 'info',
      pending: 'warning',
      suspended: 'error',
      rejected: 'error',
    };
    return <Chip label={status} size="small" color={colors[status] || 'default'} />;
  };

  // Clean up social links - remove empty strings
  const cleanSocialLinks = (links?: typeof newCreator.social_links) => {
    if (!links) return undefined;
    const cleaned: Record<string, string> = {};
    Object.entries(links).forEach(([key, value]) => {
      if (value && value.trim()) {
        cleaned[key] = value.trim();
      }
    });
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  };

  const handleAddCreator = async (asPending: boolean) => {
    if (!newCreator.email || !newCreator.name) {
      setError('Email and name are required');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const dataToSend = {
        ...newCreator,
        social_links: cleanSocialLinks(newCreator.social_links),
      };

      if (asPending) {
        await saveCreatorAsPending(dataToSend);
        setSuccessMessage('Creator saved as pending. You can approve or reject them later.');
      } else {
        await createCreator(dataToSend);
        setSuccessMessage('Creator created successfully! Welcome email sent.');
      }
      setAddDialogOpen(false);
      resetNewCreatorForm();
      fetchCreators();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (creator: Creator) => {
    setActionLoading(true);
    setError(null);
    try {
      await approveCreator(creator.creator_id);
      setSuccessMessage(`${creator.name} has been approved!`);
      fetchCreators();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedCreator || !actionReason) return;

    setActionLoading(true);
    setError(null);
    try {
      await rejectCreator(selectedCreator.creator_id, actionReason);
      setSuccessMessage(`${selectedCreator.name} has been rejected.`);
      setRejectDialogOpen(false);
      setSelectedCreator(null);
      setActionReason('');
      fetchCreators();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedCreator || !actionReason) return;

    setActionLoading(true);
    setError(null);
    try {
      await suspendCreator(selectedCreator.creator_id, actionReason, terminationType);
      setSuccessMessage(`${selectedCreator.name} has been suspended.`);
      setSuspendDialogOpen(false);
      setSelectedCreator(null);
      setActionReason('');
      setTerminationType('without_cause');
      fetchCreators();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to suspend creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendWelcome = async (creator: Creator) => {
    setActionLoading(true);
    setError(null);
    try {
      await resendCreatorWelcome(creator.creator_id);
      setSuccessMessage(`Welcome email resent to ${creator.email}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend welcome email');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Creators
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Creator
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchCreators} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Promo Code</TableCell>
              <TableCell>Code Uses</TableCell>
              <TableCell>Referrals</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : creators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  No creators found
                </TableCell>
              </TableRow>
            ) : (
              creators.map((creator) => (
                <TableRow
                  key={creator.creator_id}
                  hover
                  onClick={() => navigate(`/admin/creators/${creator.creator_id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{creator.creator_id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {creator.name}
                    </Typography>
                    {creator.niche && (
                      <Typography variant="caption" color="text.secondary">
                        {creator.niche}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{creator.email}</TableCell>
                  <TableCell>{getStatusChip(creator.status)}</TableCell>
                  <TableCell>
                    {creator.promo_code ? (
                      <Chip label={creator.promo_code} size="small" variant="outlined" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{creator.code_uses ?? 0}</TableCell>
                  <TableCell>
                    {creator.active_referrals ?? 0} / {creator.total_referrals ?? 0}
                  </TableCell>
                  <TableCell>{formatDate(creator.created_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5}>
                      {creator.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(creator)}
                              disabled={actionLoading}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedCreator(creator);
                                setRejectDialogOpen(true);
                              }}
                              disabled={actionLoading}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {(creator.status === 'approved' || creator.status === 'active') && (
                        <>
                          <Tooltip title="Resend Welcome Email">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleResendWelcome(creator)}
                              disabled={actionLoading}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Suspend">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setSelectedCreator(creator);
                                setSuspendDialogOpen(true);
                              }}
                              disabled={actionLoading}
                            >
                              <SuspendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Creator Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Creator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={newCreator.email}
              onChange={(e) => setNewCreator({ ...newCreator, email: e.target.value })}
            />
            <TextField
              label="Name"
              fullWidth
              required
              value={newCreator.name}
              onChange={(e) => setNewCreator({ ...newCreator, name: e.target.value })}
            />
            <TextField
              label="Bio"
              fullWidth
              multiline
              rows={2}
              value={newCreator.bio}
              onChange={(e) => setNewCreator({ ...newCreator, bio: e.target.value })}
            />
            <TextField
              label="Website URL"
              fullWidth
              value={newCreator.website_url}
              onChange={(e) => setNewCreator({ ...newCreator, website_url: e.target.value })}
            />
            <TextField
              label="Niche"
              fullWidth
              placeholder="e.g., food, parenting, wellness"
              value={newCreator.niche}
              onChange={(e) => setNewCreator({ ...newCreator, niche: e.target.value })}
            />
            <TextField
              label="Internal Notes"
              fullWidth
              multiline
              rows={2}
              value={newCreator.notes}
              onChange={(e) => setNewCreator({ ...newCreator, notes: e.target.value })}
            />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Social Media (optional)
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Instagram"
                size="small"
                placeholder="@username"
                value={newCreator.social_links?.instagram || ''}
                onChange={(e) => setNewCreator({
                  ...newCreator,
                  social_links: { ...newCreator.social_links, instagram: e.target.value }
                })}
              />
              <TextField
                label="TikTok"
                size="small"
                placeholder="@username"
                value={newCreator.social_links?.tiktok || ''}
                onChange={(e) => setNewCreator({
                  ...newCreator,
                  social_links: { ...newCreator.social_links, tiktok: e.target.value }
                })}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Facebook"
                size="small"
                placeholder="username or page"
                value={newCreator.social_links?.facebook || ''}
                onChange={(e) => setNewCreator({
                  ...newCreator,
                  social_links: { ...newCreator.social_links, facebook: e.target.value }
                })}
              />
              <TextField
                label="X (Twitter)"
                size="small"
                placeholder="@username"
                value={newCreator.social_links?.twitter || ''}
                onChange={(e) => setNewCreator({
                  ...newCreator,
                  social_links: { ...newCreator.social_links, twitter: e.target.value }
                })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => handleAddCreator(true)}
            disabled={actionLoading || !newCreator.email || !newCreator.name}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Save as Pending'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleAddCreator(false)}
            disabled={actionLoading || !newCreator.email || !newCreator.name}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Create & Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Creator Application</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{selectedCreator?.name}</strong>'s application?
          </Typography>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Explain why this application is being rejected..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setActionReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend Creator</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to suspend <strong>{selectedCreator?.name}</strong>? This will deactivate their promo code.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Termination Type</InputLabel>
            <Select
              value={terminationType}
              label="Termination Type"
              onChange={(e) => setTerminationType(e.target.value as 'for_cause' | 'without_cause')}
            >
              <MenuItem value="without_cause">Without cause — commissions continue 180 days</MenuItem>
              <MenuItem value="for_cause">For cause (policy violation) — commissions stop immediately</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Suspension Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Explain why this creator is being suspended..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSuspendDialogOpen(false);
            setActionReason('');
            setTerminationType('without_cause');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuspend}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Suspend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorList;
